import { rewardKeySchema } from "schemas/agent-billing";
import { setupCheckNameSchema } from "schemas/onboarding";
import { z } from "zod";

/**
 * Schemas for the Bedrock registry — the persistent registry of
 * project-authored items (templates, skills, etc.) served by
 * `apps/bedrock-api` and backed by Postgres + R2.
 *
 * Per the "PG = searchable + listing display only" rule:
 * - Top-level row columns hold identity/search/preview fields.
 * - `metadata` JSONB holds ONLY per-type fields the listing UI displays that
 *   aren't covered by standard columns. Types with no metadata store `null`.
 * - Detailed content (full file trees, npm dependencies) lives in the R2
 *   `.registry.json` doc, not the PG row.
 * - Preview imagery is referenced by external URL via `previewUrls`; the
 *   registry does not host preview blobs.
 */

export const registryItemTypeSchema = z
  .enum([
    "site",
    "page",
    "section",
    "component",
    "skill",
    "plan",
    "branding",
    "product",
    "reference",
    "canvas",
    "inspiration-section",
    // Replo-authored Printful stage geometry, one global item per provider;
    // read by the catalog sync, never installed into a project.
    "curated-stage",
  ])
  .describe("RegistryItemType");

// Stored as `tags[0]` so the agent can filter on it; the visual embedding does the ranking.
export const inspirationSectionKindSchema = z
  .enum([
    "Hero",
    "Navigation",
    "Logos",
    "Features",
    "Comparison",
    "Reviews",
    "Gallery",
    "Pricing",
    "FAQ",
    "CTA",
    "Footer",
    "Other",
  ])
  .describe("InspirationSectionKind");

// Write-boundary contract: every new item is either publicly listed or
// reachable only by direct link. Row/read schemas still type `visibility` as
// a plain string until production rows are verified to only contain these
// values.
export const registryItemVisibilitySchema = z
  .enum(["public", "unlisted"])
  .describe("RegistryItemVisibility");

// Slugs become R2 keys and public URLs, so the format is restricted to
// lowercase kebab/snake segments.
export const registrySlugSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[\da-z][\d_a-z-]*$/);

export const registryItemIdSchema = z.uuid();

export const REGISTRY_FILE_PATH_REQUIREMENT_MESSAGE =
  "Registry file paths must be canonical relative POSIX paths";

export const REGISTRY_APP_ROOT_REQUIREMENT_MESSAGE =
  "Registry paths must be canonical relative POSIX paths; page and site registry items must use root app/ and must not include src/app/";

function isCanonicalRegistryFilePath(filePath: string) {
  const segments = filePath.split("/");
  return (
    !filePath.includes("\\") &&
    segments.every((segment) => {
      return (
        segment.length > 0 &&
        segment === segment.trim() &&
        segment !== "." &&
        segment !== ".."
      );
    })
  );
}

const registryFilePathSchema = z
  .string()
  .min(1)
  .refine(isCanonicalRegistryFilePath, {
    message: REGISTRY_FILE_PATH_REQUIREMENT_MESSAGE,
  });

export const registryContentFilesSchema = z
  .record(registryFilePathSchema, z.string())
  .refine((files) => Object.keys(files).length > 0, {
    message: "Registry item content must include at least one file",
  });

export function hasRequiredRegistryAppRoot({
  files,
  type,
}: {
  files: Record<string, string>;
  type: z.infer<typeof registryItemTypeSchema>;
}) {
  const filePaths = Object.keys(files);
  if (filePaths.length === 0 || !filePaths.every(isCanonicalRegistryFilePath)) {
    return false;
  }
  if (type !== "page" && type !== "site") {
    return true;
  }
  return (
    filePaths.some((filePath) => filePath.startsWith("app/")) &&
    filePaths.every((filePath) => {
      return filePath !== "src/app" && !filePath.startsWith("src/app/");
    })
  );
}

export function validateRegistryAppRoot(
  item: {
    files: Record<string, string>;
    type: z.infer<typeof registryItemTypeSchema>;
  },
  context: z.RefinementCtx,
) {
  if (!hasRequiredRegistryAppRoot(item)) {
    context.addIssue({
      code: "custom",
      message: REGISTRY_APP_ROOT_REQUIREMENT_MESSAGE,
      path: ["files"],
    });
  }
}

export const registryAppRootRequirementSchema = z
  .object({
    files: registryContentFilesSchema,
    type: registryItemTypeSchema,
  })
  .superRefine(validateRegistryAppRoot);

export const registryItemSourceIdentitySchema = z
  .object({
    sourceType: z.string().min(1).max(80),
    sourceKey: z.string().min(1).max(512),
  })
  .describe("RegistryItemSourceIdentity");

// Display-only fields for visual templates (sites, pages, sections, ads).
// These are not searchable or filterable on the row — they're rendered on
// preview cards in the agent-ui registry browser when present.
//
// `brandDomain` is consumed client-side by `resolveBrandDisplay` to derive
// a Clearbit logo URL when no curated `logoUrl` is uploaded yet, so that
// new items can light up the brand row without a separate asset upload.
const visualDisplayMetadataSchema = z
  .object({
    brandName: z.string().optional(),
    brandDomain: z.string().optional(),
    logoUrl: z.url().optional(),
  })
  .strict();

// A written design description paired with reference screenshots — the unit
// the image-to-design pipeline sends to the model verbatim (description +
// screenshots together; either alone regresses to generic output).
export const registryDesignDescriptionSchema = z
  .object({
    description: z.string().min(1),
    aestheticFamily: z.string().min(1),
    pageType: z.string().min(1),
    screenshotUrls: z.array(z.url()).min(1).max(4),
  })
  .strict()
  .describe("RegistryDesignDescription");

// One previewable page of a site template — a live iframe-able URL on the static gallery; `route` is the in-site path (`/`, `/pricing`) and `name` is the human label shown in the preview carousel.
const siteTemplatePageSchema = z
  .object({
    route: z.string(),
    name: z.string(),
    url: z.url(),
    design: registryDesignDescriptionSchema.optional(),
  })
  .strict();

// Site templates extend the shared visual fields with two pointers: `artifactRepo` (the Cloudflare Artifacts repo to FORK on "Create Site" — the clone source, not the R2 `files` tree) and `liveUrl` + `pages` (live iframe-able URLs for the preview carousel).
export const siteRegistryItemMetadataSchema = visualDisplayMetadataSchema
  .extend({
    artifactRepo: z.string().optional(),
    liveUrl: z.url().optional(),
    pages: z.array(siteTemplatePageSchema).optional(),
  })
  .describe("SiteRegistryItemMetadata");

export const pageRegistryItemMetadataSchema = visualDisplayMetadataSchema
  .extend({
    keyFeatures: z.array(z.string().min(1).max(80)).max(6).optional(),
    sectionsIncluded: z.array(z.string().min(1)).max(12).optional(),
    design: registryDesignDescriptionSchema.optional(),
  })
  .describe("PageRegistryItemMetadata");

export const sectionRegistryItemMetadataSchema = visualDisplayMetadataSchema
  .extend({
    design: registryDesignDescriptionSchema.optional(),
  })
  .describe("SectionRegistryItemMetadata");

export const componentRegistryItemMetadataSchema =
  sectionRegistryItemMetadataSchema.describe("ComponentRegistryItemMetadata");

export const canvasRegistryItemMetadataSchema = visualDisplayMetadataSchema
  .extend({
    width: z.number().positive(),
    height: z.number().positive(),
    revision: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .describe("CanvasRegistryItemMetadata");

export const brandingRegistryItemMetadataSchema = z
  .object({
    palette: z.array(z.string()).max(8).optional(),
    logoUrl: z.url().optional(),
    faviconUrl: z.url().optional(),
    tagline: z.string().max(140).optional(),
  })
  .strict()
  .describe("BrandingRegistryItemMetadata");

export const skillRegistryItemMetadataSchema = z
  .object({
    command: z.string().optional(),
    tagline: z.string().optional(),
    // (Ezra, 2026-07-09, REPL-29107): marks a catalog skill that bedrock-api
    // preinstalls into every user's workspace skills. String-valued because
    // catalog metadata is a string record end to end.
    preinstall: z.literal("true").optional(),
  })
  .strict()
  .describe("SkillRegistryItemMetadata");

// Listing/search metadata for provider-backed commerce products (e.g. Printful
// blank catalog items). Identifies the upstream provider product and lifts
// provider-neutral facets for search: decoration methods and one facet per
// option axis (Color/Size for garments, Flavor/Count for supplements). The
// full normalized CatalogProduct document lives in the R2 content blob, not
// the PG row.
export const productRegistryItemMetadataSchema = z
  .object({
    socialImageUrl: z.url().optional(),
    provider: z.string(),
    providerDisplayName: z.string(),
    providerProductId: z.string(),
    type: z.string(),
    categoryIds: z.array(z.string()).default([]),
    defaultVariantId: z.string().nullish(),
    areaCount: z.number().default(0),
    methods: z.array(z.string()).default([]),
    optionFacets: z
      .array(
        z.object({ name: z.string(), values: z.array(z.string()) }).strict(),
      )
      .default([]),
  })
  .strict()
  .describe("ProductRegistryItemMetadata");

// Per-type `metadata` validation, keyed so callers can enforce the schema a
// specific item type requires. Used by the
// bedrock-api catalog upsert boundary and the registry-templates sync so both
// sides reject the same payloads.
// The two shapes of `reference` metadata, discriminated on `kind`.
// Conversion guidance shipped as catalog rows (Sidd's CRO corpus): faceted by
// scope so agents load exactly the slice that matches the build.
export const croGuidanceMetadataSchema = z
  .object({
    kind: z.literal("cro"),
    scope: z.enum(["universal", "offer", "vertical", "page-type"]),
    vertical: z.string().optional(),
    pageType: z.string().optional(),
  })
  .strict()
  .describe("CroGuidanceMetadata");

// Code-optional design references: a screenshot + written reference, with an
// optional pointer to the buildable site item when code exists.
export const designReferenceMetadataSchema = z
  .object({
    kind: z.literal("design"),
    aestheticFamily: z.string(),
    pageType: z.string(),
    sourceUrl: z.string().optional(),
    linkedSiteSlug: z.string().optional(),
  })
  .strict()
  .describe("DesignReferenceMetadata");

// Marks a catalog plan the Tutorials library lists. `activationTask` links it
// to the task it completes, so its reward chip and done state come from the
// activation service rather than being restated here.
export const planRegistryItemMetadataSchema = z
  .object({
    tutorial: z.literal("true").optional(),
    // The one-time credit reward this step grants, if it grants one. A step
    // without it is guidance: shown and tracked, never paid, and addable
    // without touching the billing contract.
    activationTask: rewardKeySchema.optional(),
    // Which named check decides whether this step is finished. Validated at
    // generation time against SETUP_CHECK_NAMES.
    check: setupCheckNameSchema.optional(),
    tagline: z.string().optional(),
    // What starting the tutorial asks for, in the user's voice. Falls back to
    // the tutorial's name, which rarely reads as a request.
    prompt: z.string().optional(),
    // Opens this destination instead of chat, for steps the user does
    // themselves. Resolved through TUTORIAL_DESTINATIONS.
    destination: z.string().optional(),
    // What the card's button says, in the merchant's own first-person voice
    // ("Set up my brand"). Falls back to the generic "Go to <app>" the
    // destination's mini-app name would otherwise produce.
    ctaLabel: z.string().optional(),
    // Where to go once the step is done -- "see the thing you made" rather than
    // "make it". Falls back to `destination`.
    destinationDone: z.string().optional(),
    // What the card says once the step is done. A card that changes where it
    // goes should change what it asks for. Falls back to name / tagline.
    doneName: z.string().optional(),
    doneTagline: z.string().optional(),
  })
  .strict()
  .describe("PlanRegistryItemMetadata");

// A `reference` item is either CRO guidance or a design reference; `category`
// ("cro" | "design-inspiration") is the search-time discriminator.
export const referenceRegistryItemMetadataSchema = z
  .discriminatedUnion("kind", [
    croGuidanceMetadataSchema,
    designReferenceMetadataSchema,
  ])
  .describe("ReferenceRegistryItemMetadata");

const inspirationBoundsSchema = z
  .object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
  })
  .strict();

// `previewUrls` are private R2 keys the read path presigns: desktop crop first, mobile crop second.
export const inspirationSectionMetadataSchema = z
  .object({
    sectionKind: inspirationSectionKindSchema,
    pageType: z.string(),
    sourceUrl: z.string(),
    order: z.number().int().positive(),
    bounds: z
      .object({
        desktop: inspirationBoundsSchema,
        mobile: inspirationBoundsSchema.nullable(),
      })
      .strict(),
    /** One sentence naming the brand and what the source page sells, so the vector never depends on the hostname. */
    pageSummary: z.string().optional(),
    /** A Sightglass category path the labeling model chose for the page, e.g. `/Food & Drink/Pantry`. */
    categoryPath: z.string().optional(),
  })
  .strict()
  .describe("InspirationSectionMetadata");

const MAX_INSPIRATION_SECTION_EMBEDDING_TEXT_LENGTH = 8000;

/** The words embedded beside a section's crop. Design signal only: an empty line is dropped rather than sent blank. */
export function buildInspirationSectionEmbeddingText(section: {
  name: string;
  description: string | null;
  sectionKind: string | null;
  categoryPath: string | null;
  pageSummary: string | null;
}): string {
  return [
    { label: "Section", value: section.name },
    { label: "Kind", value: section.sectionKind },
    { label: "Description", value: section.description },
    {
      label: "Category",
      value: section.categoryPath?.split("/").filter(Boolean).join(" › "),
    },
    { label: "Page", value: section.pageSummary },
  ]
    .filter((line) => Boolean(line.value?.trim()))
    .map((line) => `${line.label}: ${line.value?.trim()}`)
    .join("\n")
    .slice(0, MAX_INSPIRATION_SECTION_EMBEDDING_TEXT_LENGTH);
}

export const registryItemMetadataSchemaByType: Record<
  z.infer<typeof registryItemTypeSchema>,
  z.ZodType | null
> = {
  site: siteRegistryItemMetadataSchema,
  page: pageRegistryItemMetadataSchema,
  section: sectionRegistryItemMetadataSchema,
  component: componentRegistryItemMetadataSchema,
  skill: skillRegistryItemMetadataSchema,
  branding: brandingRegistryItemMetadataSchema,
  product: productRegistryItemMetadataSchema,
  plan: planRegistryItemMetadataSchema,
  reference: referenceRegistryItemMetadataSchema,
  canvas: canvasRegistryItemMetadataSchema,
  "inspiration-section": inspirationSectionMetadataSchema,
  "curated-stage": null,
};

export const genericRegistryItemMetadataSchema = z
  .union([
    siteRegistryItemMetadataSchema,
    pageRegistryItemMetadataSchema,
    sectionRegistryItemMetadataSchema,
    componentRegistryItemMetadataSchema,
    skillRegistryItemMetadataSchema,
    brandingRegistryItemMetadataSchema,
    productRegistryItemMetadataSchema,
    croGuidanceMetadataSchema,
    designReferenceMetadataSchema,
    planRegistryItemMetadataSchema,
    canvasRegistryItemMetadataSchema,
    inspirationSectionMetadataSchema,
  ])
  .describe("GenericRegistryItemMetadata");

// Authoring body for the internal registry-catalog endpoint (`PUT .../catalog/:type/:slug`): type-generic across registry item types, server-controlled identity omitted, global-catalog items written with null ownership, and PUT replacement semantics (omitted optional fields reset to defaults).
export const registryCatalogUpsertInputSchema = z
  .object({
    name: z.string().min(1),
    description: z.string().nullish(),
    metadata: z.record(z.string(), z.unknown()).nullish(),
    visibility: registryItemVisibilitySchema.optional(),
    tags: z.array(z.string()).optional(),
    category: z.string().nullish(),
    previewUrls: z.array(z.string()).optional(),
    previewHtml: z.string().nullish(),
    files: registryContentFilesSchema,
    dependencies: z.array(z.string()).optional(),
  })
  .describe("RegistryCatalogUpsertInput");

export type RegistryCatalogUpsertInput = z.infer<
  typeof registryCatalogUpsertInputSchema
>;
